<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">

      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <b-dropdown
            v-if="getUserPermission('EXPORT_GL_HISTORY_REPORT')"
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            :text="$t('Export')"
            variant="primary"
          >
            <b-dropdown-item @click="csvExport(csvData)">
              {{ $t('CSV') }}
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport(csvData)">
              {{ $t('Excel') }}
            </b-dropdown-item>
            <b-dropdown-item @click="printGLHistoryReport">
              {{ $t('PRINT') }}
            </b-dropdown-item>
          </b-dropdown></b-col>
        <!-- Export -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="glTableRef"
      :items="reportData"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="accountNumber"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(financialDate)="data">
        <p class="mb-0">
          {{ data.value ? formatCustomDate(data.value) : '' }}
        </p>
      </template>

      <template #cell(transactionDate)="data">
        <p class="mb-0">
          {{ data.value ? formatDateTime(data.value) : '' }}
        </p>
      </template>

      <template #cell(balance)="data">
        <span class="text-nowrap">
          {{ toBalance(data.value) }}
        </span>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      no-close-on-backdrop
      @ok="handleSubmit"
      @cancel="resetFilters"
    >
      <validation-observer ref="glHistoryRules">
        <b-form>
          <b-row class="match-height">
            <!-- Account Number -->
            <b-col md="6">
              <InputField
                id="gl-code"
                v-model="filter.glCode"
                :label="$t('GL Code')"
                :placeholder="$t('Enter GL Code')"
                type="text"
                rules="required"
                :error-name="$t('GL Code')"
              />
            </b-col>
            <!-- Account Number -->
            <!-- Branch -->
            <b-col md="6">
              <SelectField
                id="branch"
                v-model="filter.branchCode"
                :label="$t('Branch')"
                :reduce="option => option.value"
                :placeholder="$t('Select')"
                :options="branchOptions"
                item="label"
              />
            </b-col>
            <!-- Branch -->
          </b-row>

          <b-row class="match-height">
            <!-- Start Date -->
            <b-col md="6">
              <DatePickerField
                id="financial-date-from"
                v-model="filter.financialDateFrom"
                :min-date="new Date()"
                :label="$t('Financial Date (From)')"
                :config="datePickerConfig"
                :placeholder="$t('Date From')"
              />
            </b-col>
            <!-- Start Date -->
            <!-- End Date -->
            <b-col md="6">
              <DatePickerField
                id="financial-date-to"
                v-model="filter.financialDateTo"
                :min-date="new Date()"
                :label="$t('Financial Date (To)')"
                :config="datePickerConfig"
                :placeholder="$t('Date To')"
              />
            </b-col>
            <!-- End Date -->
          </b-row>

        </b-form>
      </validation-observer>
      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->
    <!-- Print Start -->
    <div
      ref="glHistoryToPrint"
    >
      <p id="toPrinter">
        <b-row
          style="width: 100%; display: flex; margin-bottom: 30px;"
        >
          <b-col
            style="width: 70%; padding-left: 10px;"
          >
            <img
              style="width: 30% !important"
              :src="userDetails.institutionLogoURL ? userDetails.institutionLogoURL : reportInfo.orgLogo"
            >
            <p
              style="font-size: 16px; font-weight: 700"
              class="mt-1"
            >
              {{ corporateInformation.registeredName }}
            </p>
            <p
              style="font-size: 14px;"
              class="mt-0"
            >
              {{ corporateInformation.registeredAddress }}
            </p>
          </b-col>
          <b-col
            style="width: 30%"
          >
            <b-card-body>
              <b-card-text>
                <table
                  align="right"
                >
                  <tbody>

                    <p style="font-size: 16px; font-weight: 700;">
                      {{ $t('GL Balance History Report') }}
                    </p>

                    <tr>
                      <td>
                        <p
                          style="font-size: 14px;"
                          class="print-color"
                        >
                          {{ $t('Financial Date:') }}
                        </p>
                      </td>
                      <td v-if="filter.financialDateFrom && filter.financialDateTo">
                        <p style="font-size: 14px; font-weight: normal;">
                          {{ `${$t('From')}: ${filter.financialDateFrom} - ${$t('To')}: ${filter.financialDateTo} ` }}
                        </p>
                      </td>
                      <td v-else>
                        <p style="font-size: 15px; font-weight: normal;">
                          {{ $t('All') }}
                        </p>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <p
                          style="font-size: 14px;"
                          class="print-color"
                        >
                          {{ $t('Branch:') }}
                        </p>
                      </td>
                      <td>
                        <p>
                          {{ summary.branch }}
                        </p></td>
                    </tr>
                    <tr>
                      <td>
                        <p
                          style="font-size: 14px;"
                          class="print-color"
                        >
                          {{ $t('GL Name') }}:
                        </p>
                      </td>
                      <td>
                        <p>
                          {{ summary.name }}
                        </p></td>
                    </tr>
                    <tr>
                      <td>
                        <p
                          style="font-size: 14px;"
                          class="print-color"
                        >
                          {{ $t('GL Code') }}:
                        </p>
                      </td>
                      <td>
                        <p>
                          {{ summary.code }}
                        </p></td>
                    </tr>
                    <tr>
                      <td>
                        <p style="font-size: 14px; color: #000; margin-top: -10px;">
                          {{ $t('Opening balance') }}:
                        </p></td>
                      <td><p style="font-size: 15px; font-weight: 800;">
                        {{ toFormatAmount(summary.openingBalance) }}
                      </p></td>
                    </tr>

                    <tr>
                      <td>
                        <p style="font-size: 14px; color: #000; margin-top: -10px;">
                          {{ $t('Total Credit') }}:
                        </p>
                      </td>
                      <td>
                        <p style="font-size: 14px; font-weight: 600;">
                          NGN{{ toFormatAmount(summary.totalCredit) }}
                        </p>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <p style="font-size: 14px; color: #000; margin-top: -10px;">
                          {{ $t('Total Debit') }}:
                        </p>
                      </td>
                      <td>
                        <p style="font-size: 14px; font-weight: 600;">
                          NGN{{ toFormatAmount(summary.totalDebit) }}
                        </p>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <p style="font-size: 14px; color: #000; margin-top: -10px;">
                          {{ $t('Closing balance') }}:
                        </p></td>
                      <td><p style="font-size: 15px; font-weight: 800;">
                        {{ toFormatAmount(summary.closingBalance) }}
                      </p></td>
                    </tr>
                  </tbody>
                </table>
              </b-card-text>
            </b-card-body>
          </b-col>
        </b-row>
        <b-table
          ref="glBalanaceHistoryReportTable"
          select-mode="single"
          :items="reportPrintData"
          responsive
          no-local-sorting
          :fields="translatedPrintTableHeaders"
          primary-key="id"
          show-empty
          :empty-text="$t('No result found')"
          class="position-relative mt-2"
        >
          <template #table-busy>
            <div class="text-center text-danger my-2">
              <b-spinner
                class="align-middle"
                variant="primary"
              />
            </div>
          </template>
          <template #cell(SN)="data">
            {{ getOverallIndex(data.index) }}
          </template>

          <template #cell(financialDate)="data">
            <p class="mb-0">
              {{ data.value ? formatCustomDate(data.value) : '' }}
            </p>
          </template>

          <template #cell(transactionDate)="data">
            <p class="mb-0">
              {{ data.value ? formatDateTime(data.value) : '' }}
            </p>
          </template>

          <template #cell(balance)="data">
            <span class="text-nowrap">
              {{ toBalance(data.value) }}
            </span>
          </template>
        </b-table>
      </p>
    </div>
    <!-- Print Start-->
  </b-card>

</template>

<script>
import {
  BCard, BCardBody, BCardText, BRow, BCol, BTable, BForm, BSpinner,
  BModal, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { tableMixins } from '@core/mixins/table'
import { permissionMixin } from '@core/mixins/permission'
import { modalMixins } from '@core/mixins/modal'
import { mapGetters, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
import { heightFade } from '@core/directives/animations'
import { formatAmount, toFormatAmount, toBalance } from '@/helpers/formatAmount'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import DatePickerField from '../../../forms/DatePickerField.vue'
import InputField from '../../../forms/InputField.vue'
import SelectField from '../../../forms/SelectField.vue'
import Button from '../../../components/button/Button.vue'
import toNumber from '../../../../helpers/toNumber'
import { toCustomDateTime, toCustomDate } from '../../../../helpers/formatDate'
import { reportDataDetails } from '../../../../helpers/data'
import { reportPermissions } from '../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BCardBody,
    BCardText,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    BForm,
    TablePagination,
    TableEntries,
    InputField,
    SelectField,
    BModal,
    BDropdown,
    BDropdownItem,
    ValidationObserver,
    DatePickerField,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
  },
  mixins: [modalMixins, tableMixins, permissionMixin, sweetAlert],
  data() {
    return {
      formatAmount,
      toFormatAmount,
      toBalance,
      query: '',
      statusToggleLabel: '',
      isReadonly: false,
      buttonLabel: this.$t('Save'),
      isEditable: false,
      isBusy: false,
      required,
      modalTitle: this.$t('Customer Account History Report'),
      sortBy: 'name',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'financialDate', sortable: true, label: 'Financial Date' },
        { key: 'transactionDate', sortable: true, label: 'Transaction Date' },
        { key: 'glCode', sortable: true, label: 'GL Code' },
        { key: 'glName', sortable: true, label: 'GL Name' },
        { key: 'postedBy', sortable: true, label: 'Posted By' },
        { key: 'postingReferenceNumber', sortable: true, label: 'Posting Reference Number' },
        { key: 'debit', sortable: true, label: 'Debit' },
        { key: 'credit', sortable: true, label: 'Credit' },
        { key: 'branch', sortable: true, label: 'Branch Name' },
        { key: 'narration', sortable: true, label: 'Narration' },
        { key: 'entryCode', sortable: true, label: 'Entry Code' },
        { key: 'instrumentNumber', sortable: true, label: 'Instrument Number' },
        { key: 'balance', sortable: true, label: 'Balance' },
      ],
      printTableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'financialDate', label: 'Financial Date' },
        { key: 'transactionDate', label: 'Transaction Date' },
        // { key: 'glCode', label: 'GL Code' },
        // { key: 'glName', label: 'GL Name' },
        { key: 'postedBy', label: 'Posted By' },
        { key: 'postingReferenceNumber', label: 'Posting Reference Number' },
        { key: 'debit', label: 'Debit' },
        { key: 'credit', label: 'Credit' },
        { key: 'branch', label: 'Branch Name' },
        { key: 'narration', label: 'Narration' },
        { key: 'entryCode', label: 'Entry Code' },
        { key: 'instrumentNumber', label: 'Instrument Number' },
        { key: 'balance', label: 'Balance' },
      ],
      filter: {
        glCode: '',
        financialDateFrom: null,
        financialDateTo: null,
        branchCode: '',
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        maxDate: 'today',
        defaultDate: '',
      },
      reportData: [],
      reportPrintData: [],
      summary: {},
      allSearchedData: [],
      selected: '',
      reportInfo: reportDataDetails,

    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
      userDetails: 'auth/getCurrentUser',
      corporateInformation: 'operations/getCorporateDetails',
    }),
    csvData() {
      return this.exportItems.map(item => ({
        ...item,
      }))
    },
    exportItems() {
      return this.allSearchedData.map((item, index) => ({
        'S/N': index + 1,
        'Financial Date': this.formatCustomDate(item.financialDate),
        'Transaction Date': this.formatDateTime(item.transactionDate),
        'GL Code': item.glCode,
        'GL Name': item.glName,
        'Posted By': item.postedBy,
        'Posting Ref. No.': item.postingReferenceNumber,
        Debit: item.debit === 0 ? '' : formatAmount(item.debit),
        Credit: item.credit === 0 ? '' : formatAmount(item.credit),
        'Branch Name': item.branch,
        Narration: item.narration,
        'Sort Code': item.entryCode,
        'Instrument Number': item.instrumentNumber,
        Balance: formatAmount(item.balance),
      }))
    },
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedPrintTableHeaders() {
      return this.printTableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchGLBalanceHistoryReport()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchGLBalanceHistoryReport()
    },
  },
  mounted() {
    // this.fetchGLBalanceHistoryReport()
  },
  methods: {
    ...mapActions({
      getGLBalanceHistories: 'reports/getGLBalanceHistories',
      downloadGLBalanceHistoryReport: 'reports/downloadGLBalanceHistoryReport',
    }),

    async printGLHistoryReport() {
      try {
        this.isBusy = true
        const response = await this.getGLBalanceHistories({
          search: this.filter,
          page: 1,
          limit: 1000000,
        })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.reportPrintData = data.data.map(item => ({
            ...item,
            debit: item.debit ? toBalance(item.debit) : '',
            credit: item.credit ? toBalance(item.credit) : '',
          }))
          this.summary = {
            ...data.summary,
            branch: data.summary.branch ? data.summary.branch : 'All',
          }
          this.totalCount = 0
          setTimeout(() => {
            const { glHistoryToPrint } = this.$refs
            const printWindow = window.open('', '_blank')
            printWindow.document.write(glHistoryToPrint.innerHTML)
            const styleElement = printWindow.document.createElement('style')
            styleElement.textContent = `
        body {
          font-family: 'Arial', sans-serif;
          color: #333;
        }
        /* Set A3 size */
          //  @page {
          //   size: A3;
          //   height: 100%;
          //   margin: 5px;
          //   }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 20px;
        }

        /* Apply styles to table headers */
        th {
          background-color: #cccccc !important;
          text-transform: uppercase;
          font-size: 14px !important;
        }

        /* Apply styles to table cells */
        td, th {
          padding: 5px;
          text-align: left;
        }

        /* Apply alternating row colors */
        tr:nth-child(even) {
          background-color: #f9f9f9;
        }

        /* Apply hover effect to rows */
        tr:hover {
          background-color: #e5e5e5;
        }
      `
            printWindow.document.head.appendChild(styleElement)
            setTimeout(() => {
              printWindow.document.close()
              printWindow.print()
              printWindow.close()
            }, 1000)
          }, 1000)
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.filter.glCode = null
      this.fetchGLBalanceHistoryReport()
    },

    async handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.glHistoryRules.validate().then(async success => {
        if (success) {
          this.fetchGLBalanceHistoryReport()
        }
      })
    },
    async fetchGLBalanceHistoryReport() {
      try {
        this.hideModal('modal-filter')
        this.isBusy = true
        const response = await this.getGLBalanceHistories({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
        })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.reportData = data.data.map(item => ({
            ...item,
            debit: item.debit ? toBalance(item.debit) : '',
            credit: item.credit ? toBalance(item.credit) : '',
          }))
          this.summary = {
            ...data.summary,
            branch: data.summary.branch ? data.summary.branch : 'All',
          }
          this.totalCount = data.recordCount
        } else {
          this.reportData = []
          this.totalCount = 0
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },

    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.downloadGLBalanceHistoryReport({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          fileExportFormat: 1,
          permission: reportPermissions.EXPORT_GL_HISTORY_REPORT,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: this.$t('Your download is in progress and might take a little longer. Go to your'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('to access the completed file.'),
          })
        } else if (response.status === 504) {
          this.alertSuccessCustom({
            text: this.$t('Your download request is still processing, keep checking the'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('for status.'),
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.downloadGLBalanceHistoryReport({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          fileExportFormat: 0,
          permission: reportPermissions.EXPORT_GL_HISTORY_REPORT,
        })
        if (response.status === 200) {
          this.alertSuccessCustom({
            text: this.$t('Your download is in progress and might take a little longer. Go to your'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('to access the completed file.'),
          })
        } else if (response.status === 504) {
          this.alertSuccessCustom({
            text: this.$t('Your download request is still processing, keep checking the'),
            link: {
              text: this.$t('Download Manager'),
              route: { name: 'download-manager' },
            },
            afterLinkText: this.$t('for status.'),
          })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },

    formatNumber: toNumber,
    formatDateTime: toCustomDateTime,
    formatCustomDate: toCustomDate,
  },
}
</script>

<style lang="scss" scoped>

#toPrinter {
  display: none;
}
    .per-page-selector {
        width: 90px;
    }

    .invoice-filter-select {
        min-width: 190px;

        ::v-deep .vs__selected-options{
            flex-wrap: nowrap;
        }

        ::v-deep .vs__selected {
            width: 100px;
        }
    }
</style>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
    @media print {
  html, body {
     height: 99%;
     page-break-after: avoid !important;
     page-break-before: avoid !important;
     visibility:hidden;
  }
  #toPrinter {
    display: block;
  }
}
.print-color {
  color: #000000;
}
img {
  max-width: 50%;
  height: auto;
}
</style>
